'use strict';

/**
 * paymentTerminal.js — Abstracción multi-proveedor para terminales de pago
 *
 * Enruta a Stripe Terminal o Mercado Pago Point según el campo
 * `terminal_provider` del PosTerminal almacenado en sessionStore.
 *
 * Uso en CheckoutScreen:
 *   import paymentTerminal from '../native/paymentTerminal';
 *   const result = await paymentTerminal.charge({ amount, terminal, externalRef });
 *   // result: { providerPaymentId, provider }
 */

import { stripeTerminal } from './stripeTerminal.js';
import {
  createTerminalPaymentIntent,
  createMpPaymentIntent,
  getMpPaymentIntentStatus,
  cancelMpPaymentIntent,
} from '../api/pos.api.js';

// ─── Mercado Pago — polling hasta que el cobro termine ───────────────────────

const MP_POLL_MS      = 2_000;   // cada 2 s
const MP_TIMEOUT_MS   = 180_000; // 3 min máximo

const TERMINAL_STATES = new Set(['PROCESSING', 'PROCESSED', 'CANCELED', 'ABANDONED', 'ERROR']);

async function pollMpIntent(intentId, signal) {
  const deadline = Date.now() + MP_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (signal?.aborted) throw new Error('Cobro cancelado');

    const { state, paymentId } = await getMpPaymentIntentStatus(intentId);

    if (state === 'PROCESSED') return { providerPaymentId: String(paymentId), provider: 'mercadopago' };
    if (state === 'CANCELED')  throw new Error('El cliente canceló el cobro en el lector');
    if (state === 'ABANDONED') throw new Error('Tiempo agotado en el lector');
    if (state === 'ERROR')     throw new Error('Error en el lector de Mercado Pago');

    await new Promise((r) => setTimeout(r, MP_POLL_MS));
  }

  throw new Error('Tiempo de espera agotado esperando confirmación de Mercado Pago');
}

// ─── API pública ──────────────────────────────────────────────────────────────

const paymentTerminal = {
  /**
   * Cobra `amount` (MXN) en el lector configurado en `terminal`.
   *
   * @param {object} opts
   * @param {number} opts.amount            — Monto en pesos (ej. 199.50)
   * @param {object} opts.terminal          — PosTerminal del sessionStore
   * @param {string} [opts.externalRef]     — Referencia externa (folio del pago)
   * @param {AbortSignal} [opts.signal]     — Para cancelar desde la UI
   * @param {function} [opts.onPhase]       — Callback de fase ('preparing'|'waiting'|'processing')
   *
   * @returns {{ providerPaymentId: string, provider: string }}
   */
  async charge({ amount, terminal, externalRef, signal, onPhase }) {
    const provider = terminal?.terminal_provider ?? 'stripe';

    // ── Stripe Terminal ────────────────────────────────────────────────────────
    if (provider === 'stripe') {
      if (!stripeTerminal.isConnected()) {
        throw new Error('No hay lector Stripe conectado. Ve a Configuración → Lectores.');
      }

      onPhase?.('preparing');
      const { clientSecret, paymentIntentId } = await createTerminalPaymentIntent(amount);

      onPhase?.('waiting');
      await stripeTerminal.collectPayment({ clientSecret });

      onPhase?.('processing');
      return { providerPaymentId: paymentIntentId, provider: 'stripe' };
    }

    // ── Mercado Pago Point ─────────────────────────────────────────────────────
    if (provider === 'mercadopago') {
      const deviceId = terminal?.mp_device_id;
      if (!deviceId) {
        throw new Error('La terminal no tiene un lector MP asignado. Configúrala en Ajustes → POS.');
      }

      onPhase?.('preparing');
      const { intentId } = await createMpPaymentIntent({
        amount,            // MP Point Integration API recibe pesos MXN
        device_id:         deviceId,
        external_reference: externalRef,
      });

      onPhase?.('waiting');
      try {
        const result = await pollMpIntent(intentId, signal);
        onPhase?.('processing');
        return result;
      } catch (err) {
        // Intentar cancelar el intent en MP para liberar el lector
        try { await cancelMpPaymentIntent(intentId, deviceId); } catch { /* ignore */ }
        throw err;
      }
    }

    throw new Error(`Proveedor de terminal desconocido: "${provider}"`);
  },

  /**
   * Cancela un cobro en curso.
   * Solo relevante para Stripe (MP se cancela vía polling + DELETE).
   */
  async cancel({ terminal }) {
    const provider = terminal?.terminal_provider ?? 'stripe';
    if (provider === 'stripe') {
      await stripeTerminal.cancelCollect();
    }
    // Para MP, el abort vía signal + DELETE en pollMpIntent maneja la cancelación
  },
};

export default paymentTerminal;
